import React, { useState, useEffect, useRef } from "react";
import { applyAppLanguageFromStorage } from "./utils/appLanguage";
import Header from "./component/Header";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { ReactNotifications } from "react-notifications-component";
import "react-notifications-component/dist/theme.css";
import Home from "./pages/Home";
import BottomHeader from "./component/BottomHeader";
import News from "./pages/News";
import Feed from "./pages/Feed";
import ListingLayout from "./pages/ListingLayout";
import ListingLayoutConsumer from "./pages/ListingLayoutConsumer";
import Profile from "./pages/Profile";
import PremiumHome from "./pages/PremiumHome";
import Login from "./pages/Login";
import PortfolioDetails from "./pages/PortfolioDetails";
import IncomingMemberFeed from "./pages/IncomingMemberFeed";
import ProtectedRoute from "./component/ProtectedRoute";
import Notifications from "./pages/Notifications";
import ForgotPassword from "./pages/ForgotPassword";
import Signup from "./pages/Signup";
import PortfolioCreateFilter from "./pages/PortfolioCreateFilter";
import NewAvailableFilter from "./pages/NewAvailableFilter";
import CreateNewAvailableFilter from "./pages/CreateNewAvailableFilter";
import ConsumerAppActive from "./pages/ConsumerAppActive";
import ConsumerAppSettings from "./pages/ConsumerAppSettings";
import InfoRequests from "./pages/InfoRequests";
import ConsumerProfile from "./pages/ConsumerProfile";
import ConsumerList from "./pages/ConsumerList";
import ConsumerFilter from "./pages/ConsumerFilter";
import MemberFilter100 from "./pages/MemberFilter100";
import MemberCreateFilter100 from "./pages/MemberCreateFilter100";

function ConsumerProfileLegacyRedirect() {
    const loc = useLocation();
    return <Navigate to="/premium-home/consumer-profile" replace state={loc.state} />;
}

// Pages where Header and BottomHeader should be hidden
const HIDE_HEADER_PAGES = [
    '/',
    '/login',
    '/signup',
    '/forgot-password',
    '/listing-layout',
    '/home/my-connection',
    '/home/connection-request',
    '/home/global-members',
    '/home/member-filter',
    '/home/member-filter-my-connection',
    '/home/blocked-members',
    '/home/my-listings',
    '/home/my-portfolio',
    '/home/global-portfolio',
    '/home/portfolio-filter',
    '/home/portfolio-favorite',
    '/member-profile',
    '/home/global-members-details',
    '/home/member-create-filter',
    '/home/portfolio-create-filter',
    '/home/add-listing',
    '/home/edit-listing',
    '/home/inactive-listing',
    '/home/my-listing-contract',
    '/portfolio-details',
    '/feed',
    '/feed/new-available',
    '/feed/feed-recived',
    '/feed/feeds-requested',
    '/feed/my-feed-shared',
    '/feed/my-feed-proposed',
    '/feed/my-feed-active',
    '/feed/incoming-member-feed',
    '/feed/view-my-shared',
    '/feed/connection-feed-settings',
    '/feed/connection-feed-settings-details',
    '/feed/new-available-filter',
    '/feed/create-new-available-filter',
    '/profile',
    '/premium-home/premium-members',
    '/premium-home/consumer-apps-active',
    '/premium-home/consumer-app-settings',
    '/consumer-app-active',
    '/consumer-app-settings',
    '/premium-home/info-requests',
    '/premium-home/listing-match',
    '/premium-home/member-filter-100',
    '/premium-home/member-create-filter-100',
    '/premium-home/consumer-list',
    '/premium-home/consumer-profile',
    '/premium-home/consumer-filter',
    '/info-requests',
];

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const isLoggedIn = Boolean(localStorage.getItem('user_id'));
    const [isMobile, setIsMobile] = useState(false);
    const [isViewingMyConnection, setIsViewingMyConnection] = useState(false);
    const [appLangVersion, setAppLangVersion] = useState(0);
    const ignoreNextEventRef = useRef(false);
    const didHandleInitialLoggedInRedirectRef = useRef(false);

    useEffect(() => {
        const onLang = () => {
            applyAppLanguageFromStorage();
            setAppLangVersion((v) => v + 1);
        };
        window.addEventListener('appLanguageChanged', onLang);
        return () => window.removeEventListener('appLanguageChanged', onLang);
    }, []);

    // Routes that don't require authentication (exact or prefix match; '/' only for exact root)
    const isPublicRoute = (path) => {
        if (path === '/' || path === '/login') return true;
        if (path.startsWith('/signup') || path.startsWith('/forgot-password')) return true;
        return false;
    };

    // Redirect to login if not authenticated and trying to access any protected route
    useEffect(() => {
        const currentPath = location.pathname;
        if (isPublicRoute(currentPath)) return;
        const userId = localStorage.getItem('user_id');
        if (!userId) {
            navigate('/login', { replace: true, state: { from: location } });
        }
    }, [location.pathname, navigate, location]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 767);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (didHandleInitialLoggedInRedirectRef.current) return;
        didHandleInitialLoggedInRedirectRef.current = true;

        if (!isLoggedIn) return;
        if (location.pathname !== '/home') {
            navigate('/home', { replace: true });
        }
    }, [isLoggedIn, location.pathname, navigate]);

    // Reset isViewingMyConnection when location changes
    useEffect(() => {
        const path = location.pathname.toLowerCase();
        const isHomePage = path === '/home' || path.startsWith('/home/');
        // Only /home/my-connection should hide BottomHeader, not /home (sidebar view)
        const isMyConnectionPage = path === '/home/my-connection';

        // Reset state when navigating away from home page
        if (!isHomePage) {
            setIsViewingMyConnection(false);
            ignoreNextEventRef.current = false;
        } else {
            // Set based on current route - only /home/my-connection hides BottomHeader
            setIsViewingMyConnection(isMyConnectionPage);
            ignoreNextEventRef.current = true;
            // Reset the flag after a short delay to allow Home component to mount
            const timer = setTimeout(() => {
                ignoreNextEventRef.current = false;
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [location.pathname]);

    // Listen for custom event from Home component when MyConnection is viewed
    useEffect(() => {
        const handleMyConnectionView = (e) => {
            // Only update if we're on a home page route
            const path = location.pathname.toLowerCase();
            const isHomePage = path === '/home' || path.startsWith('/home/');
            if (isHomePage) {
                // Ignore the first event when navigating to home (initial mount)
                if (ignoreNextEventRef.current) {
                    return;
                }
                setIsViewingMyConnection(e.detail.isViewing);
            }
        };

        window.addEventListener('myConnectionView', handleMyConnectionView);
        return () => window.removeEventListener('myConnectionView', handleMyConnectionView);
    }, [location.pathname]);

    // Check if current page should hide headers
    // For /home/* routes: hide header only on mobile (desktop always shows header)
    const shouldHideHeaders = () => {
        const path = location.pathname.toLowerCase();
        return HIDE_HEADER_PAGES.some(hidePath => {
            if (hidePath === '/') {
                return path === '/' || path === '/login';
            }
            if (
                hidePath.startsWith('/home') ||
                hidePath === '/member-profile' ||
                hidePath === '/home/global-members-details' ||
                hidePath === '/listing-layout' ||
                hidePath.startsWith('/premium-home')
            ) {
                const isMatch = path === hidePath || path.startsWith(hidePath);
                return isMatch && isMobile;
            }
            // Feed subroutes: hide header only on mobile (desktop shows header)
            if (
                hidePath === '/feed' ||
                hidePath === '/feed/new-available' ||
                hidePath === '/feed/feed-recived' ||
                hidePath === '/feed/feeds-requested' ||
                hidePath === '/feed/my-feed-shared' ||
                hidePath === '/feed/my-feed-proposed' ||
                hidePath === '/feed/my-feed-active' ||
                hidePath === '/feed/incoming-member-feed' ||
                hidePath === '/feed/view-my-shared' ||
                hidePath === '/feed/connection-feed-settings' ||
                hidePath === '/feed/connection-feed-settings-details' ||
                hidePath === '/feed/new-available-filter' ||
                hidePath === '/feed/create-new-available-filter' ||
                hidePath === '/profile' ||
                hidePath === '/consumer-app-active' ||
                hidePath === '/consumer-app-settings' ||
                hidePath === '/info-requests'
            ) {
                const isMatch = path === hidePath || path.startsWith(hidePath);
                return isMatch && isMobile;
            }
            return path === hidePath || path.startsWith(hidePath);
        });
    };

    // Check if Header should be shown
    const shouldShowHeader = () => {
        return !shouldHideHeaders();
    };

    // Check if BottomHeader should be shown (protected route + mobile only)
    const shouldShowBottomHeader = () => {
        // Only show on mobile devices
        if (!isMobile) return false;

        // Listing layout uses its own bottom bar
        const layoutPath = location.pathname.toLowerCase();
        if (layoutPath === '/listing-layout' || layoutPath === '/listing-layout-consumer') return false;

        // Member Filter uses its own bottom gear menu
        if (location.pathname.toLowerCase() === '/home/member-filter') return false;
        if (location.pathname.toLowerCase() === '/home/member-filter-my-connection') return false;
        if (location.pathname.toLowerCase() === '/premium-home/member-filter-100') return false;
        if (location.pathname.toLowerCase() === '/premium-home/member-create-filter-100') return false;
        if (location.pathname.toLowerCase() === '/premium-home/consumer-filter') return false;

        // Feed pages use page-specific mobile bottom nav
        if (location.pathname.toLowerCase().startsWith('/feed')) return false;

        // Hide on protected pages (same as Header)
        if (shouldHideHeaders()) return false;

        // Hide when viewing MyConnection
        if (isViewingMyConnection) return false;

        return true;
    };

    return (
        <div data-app-lang={appLangVersion}>
            <ReactNotifications />
            {shouldShowHeader() && <Header />}
            <Routes>
                <Route path="/" element={isLoggedIn ? <Navigate to="/home" replace /> : <Login />} />
                <Route path="/login" element={isLoggedIn ? <Navigate to="/home" replace /> : <Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* Protected Routes - require authentication */}
                <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                {/* Mobile routes - only used on mobile devices */}
                <Route path="/home/my-connection" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/connection-request" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/connection-request-send" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/global-members" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/global-members-details" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/member-filter" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/member-filter-my-connection" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/my-connection-create-filter" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/member-create-filter" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/add-listing" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/edit-listing" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/blocked-members" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/my-listings" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/inactive-listing" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/my-listing-contract" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/my-portfolio" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/global-portfolio" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/portfolio-filter" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/portfolio-favorite" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/home/portfolio-create-filter" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
                <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/new-available" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/feed-recived" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/feeds-requested" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-shared" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-proposed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active/feed-1" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active/feed-2" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active/feed-3" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active/feed-4" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active/feed-5" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/my-feed-active/pocket-feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/incoming-member-feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/view-my-shared" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/connection-feed-settings" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/connection-feed-settings-details" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                <Route path="/feed/new-available-filter" element={<ProtectedRoute><NewAvailableFilter /></ProtectedRoute>} />
                <Route path="/feed/create-new-available-filter" element={<ProtectedRoute><CreateNewAvailableFilter /></ProtectedRoute>} />
                <Route path="/listing-layout" element={<ProtectedRoute><ListingLayout /></ProtectedRoute>} />
                <Route path="/listing-layout-consumer" element={<ProtectedRoute><ListingLayoutConsumer /></ProtectedRoute>} />
                <Route path="/portfolio-details" element={<ProtectedRoute><PortfolioDetails /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/change-password" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/change-email" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/profile/web-settings" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/member-profile" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/premium-home/member-filter-100" element={<ProtectedRoute><MemberFilter100 /></ProtectedRoute>} />
                <Route path="/premium-home/member-create-filter-100" element={<ProtectedRoute><MemberCreateFilter100 /></ProtectedRoute>} />
                <Route path="/premium-home/consumer-list" element={<ProtectedRoute><ConsumerList /></ProtectedRoute>} />
                <Route path="/premium-home/consumer-profile" element={<ProtectedRoute><ConsumerProfile /></ProtectedRoute>} />
                <Route path="/premium-home/consumer-filter" element={<ProtectedRoute><ConsumerFilter /></ProtectedRoute>} />
                <Route path="/premium-home/*" element={<ProtectedRoute><PremiumHome /></ProtectedRoute>} />
                <Route path="/consumer-app-active" element={<ProtectedRoute><ConsumerAppActive /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                <Route path="/consumer-app-settings" element={<ProtectedRoute><ConsumerAppSettings /></ProtectedRoute>} />
                <Route path="/info-requests" element={<ProtectedRoute><InfoRequests /></ProtectedRoute>} />
                <Route path="/consumer-profile" element={<ProtectedRoute><ConsumerProfileLegacyRedirect /></ProtectedRoute>} />
                <Route path="/consumer-list" element={<ProtectedRoute><Navigate to="/premium-home/consumer-list" replace /></ProtectedRoute>} />
                <Route path="/member-filter-100" element={<ProtectedRoute><Navigate to="/premium-home/member-filter-100" replace /></ProtectedRoute>} />
                <Route path="/member-create-filter-100" element={<ProtectedRoute><Navigate to="/premium-home/member-create-filter-100" replace /></ProtectedRoute>} />
            </Routes>
            {shouldShowBottomHeader() && <BottomHeader />}
        </div>
    );
}

export default App;

